const compliancePoints = [
  {
    icon: 'ri-shield-check-line',
    title: 'Medical Access & Services Only',
    description: 'Membership covers medical access and services. All medications are prescribed and dispensed separately by licensed pharmacies.',
  },
  {
    icon: 'ri-hospital-line',
    title: 'Licensed Medical Professionals',
    description: 'All care is provided by Nevada-licensed healthcare providers following state and federal regulations.',
  },
  {
    icon: 'ri-file-text-line',
    title: 'No Medication Resale',
    description: 'We do not resell or repackage medications. All prescriptions are filled through licensed pharmacies.',
  },
  {
    icon: 'ri-map-pin-line',
    title: 'Nevada Regulatory Compliance',
    description: 'Fully compliant with Nevada State Board of Medical Examiners and all applicable healthcare regulations.',
  },
];

const whoItsFor = [
  {
    icon: 'ri-briefcase-4-line',
    title: 'Professionals Who Value Time',
    desc: 'Busy executives who need efficient, high-quality healthcare without the wait.',
  },
  {
    icon: 'ri-parent-line',
    title: 'Families Who Want Consistency',
    desc: 'Parents seeking coordinated, continuous care for their entire family unit.',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Patients Tired of Fragmentation',
    desc: 'Individuals frustrated with disconnected care and lack of provider continuity.',
  },
  {
    icon: 'ri-shield-star-line',
    title: 'Prevention-Focused Individuals',
    desc: 'People who prioritize wellness and prevention over reactive crisis management.',
  },
];

const promises = [
  'Care Worth Signing For.',
  'Your Health, Signed and Sealed.',
  'A Higher Standard of Care.',
  'Medicine With Accountability.',
];

export default function ComplianceAndRegulations() {
  return (
    <section id="compliance" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-16">
        {/* Who it's for */}
        <div className="mb-24">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-teal-600 mb-4">
              Our Ideal Patient
            </span>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-bold text-[#0F1C2E] mb-5">
              Who Signature™ Is For
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Signature is designed for discerning patients who demand more from their healthcare experience
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whoItsFor.map((item) => (
              <div
                key={item.title}
                className="group bg-[#F8FAFB] border border-gray-100 rounded-2xl p-7 hover:border-teal-200 hover:-translate-y-1 transition-all duration-300 cursor-default"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 group-hover:bg-teal-600 rounded-xl mb-5 transition-colors duration-300">
                  <i className={`${item.icon} text-xl text-teal-600 group-hover:text-white transition-colors duration-300`}></i>
                </div>
                <h3 className="font-bold text-[#0F1C2E] mb-2 text-sm leading-tight">{item.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Signature Promise */}
        <div className="mb-24 bg-gradient-to-br from-[#F0FDF9] to-[#F8FAFB] border border-teal-100 rounded-2xl p-10 lg:p-14">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-block text-xs font-semibold tracking-widest uppercase text-teal-600 mb-4">
                Our Promise
              </span>
              <h3 className="font-serif-display text-3xl sm:text-4xl font-bold text-[#0F1C2E] mb-8">
                Our Signature Promise
              </h3>
              <div className="space-y-4">
                {promises.map((phrase) => (
                  <div key={phrase} className="flex items-center gap-4">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <i className="ri-quill-pen-line text-teal-600 text-lg"></i>
                    </div>
                    <span className="text-base font-medium text-[#0F1C2E]">{phrase}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: 'ri-heart-pulse-line', label: 'Preventive Care' },
                { icon: 'ri-shield-check-line', label: 'HIPAA Compliant' },
                { icon: 'ri-award-line', label: 'Nevada Licensed' },
                { icon: 'ri-user-star-line', label: 'Named Provider' },
              ].map((badge) => (
                <div key={badge.label} className="bg-white border border-gray-100 rounded-xl p-5 flex flex-col items-center text-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-lg">
                    <i className={`${badge.icon} text-teal-600 text-lg`}></i>
                  </div>
                  <span className="text-xs font-semibold text-[#0F1C2E]">{badge.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Compliance */}
        <div>
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-teal-600 mb-4">
              Trust &amp; Transparency
            </span>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-bold text-[#0F1C2E] mb-5">
              Signature Compliance Promise
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              We protect patients and the practice through rigorous adherence to medical regulations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {compliancePoints.map((point) => (
              <div
                key={point.title}
                className="flex items-start gap-5 bg-[#F8FAFB] rounded-2xl p-7 border border-gray-100 hover:border-teal-200 transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-xl flex-shrink-0">
                  <i className={`${point.icon} text-xl text-teal-600`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#0F1C2E] mb-2 text-base">{point.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{point.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Important notice */}
          <div className="bg-[#0A2818] rounded-2xl p-8 lg:p-10 flex items-start gap-6">
            <div className="w-10 h-10 flex items-center justify-center bg-teal-500/20 rounded-lg flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-teal-400 text-xl"></i>
            </div>
            <div>
              <h3 className="text-white font-bold mb-3 text-base">Important Compliance Information</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Signature™ membership provides access to medical services and provider relationships.
                All medications are prescribed based on medical necessity and dispensed through licensed,
                independent pharmacies. Medication costs are separate from membership fees. We maintain
                full compliance with Nevada healthcare regulations and federal guidelines.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
